import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Star, MessageSquare, Send } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealtorRatings = ({ user }) => {
  const [myRatings, setMyRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responseDialog, setResponseDialog] = useState({ open: false, rating: null });
  const [explanation, setExplanation] = useState('');
  const [rateCustomerDialog, setRateCustomerDialog] = useState({ open: false, lead: null });
  const [customerRating, setCustomerRating] = useState(0);
  const [customerComment, setCustomerComment] = useState('');
  const [myLeads, setMyLeads] = useState([]);

  useEffect(() => {
    fetchMyRatings();
    fetchMyLeads();
  }, []);

  const fetchMyRatings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/realtor/my-ratings`, { withCredentials: true });
      setMyRatings(response.data.ratings || []);
    } catch (error) {
      toast.error('Failed to load ratings');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyLeads = async () => {
    try {
      const response = await axios.get(`${API}/realtor/my-leads`, { withCredentials: true });
      setMyLeads(response.data.leads || []);
    } catch (error) {
      console.error('Failed to load leads');
    }
  };

  const submitExplanation = async () => {
    if (!explanation.trim()) {
      toast.error('Please enter an explanation');
      return;
    }

    try {
      await axios.post(
        `${API}/realtor/respond-to-rating`,
        {},
        {
          params: {
            rating_id: responseDialog.rating.id,
            explanation: explanation
          },
          withCredentials: true
        }
      );
      toast.success('Response submitted successfully');
      setResponseDialog({ open: false, rating: null });
      setExplanation('');
      fetchMyRatings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit response');
    }
  };

  const submitCustomerRating = async () => {
    if (customerRating === 0) {
      toast.error('Please select a rating');
      return;
    }

    try {
      await axios.post(
        `${API}/realtor/rate-customer`,
        {},
        {
          params: {
            lead_id: rateCustomerDialog.lead.id,
            rating: customerRating,
            comment: customerComment || null
          },
          withCredentials: true
        }
      );
      toast.success('Customer rating submitted successfully');
      setRateCustomerDialog({ open: false, lead: null });
      setCustomerRating(0);
      setCustomerComment('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit rating');
    }
  };

  const renderStars = (rating, interactive = false, onRate = null) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
            } ${interactive ? 'cursor-pointer hover:scale-110 transition-transform' : ''}`}
            onClick={() => interactive && onRate && onRate(star)}
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Rate Customer Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Rate Your Customers</h3>
        {myLeads.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No leads assigned yet</p>
        ) : (
          <div className="grid gap-4">
            {myLeads.slice(0, 5).map((lead) => (
              <div key={lead.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">{lead.lead_name}</p>
                  <p className="text-sm text-gray-600">{lead.property_address}</p>
                  <p className="text-xs text-gray-500">{lead.lead_email}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => setRateCustomerDialog({ open: true, lead })}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Star className="w-4 h-4 mr-1" />
                  Rate Customer
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* My Ratings Section */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4">Ratings I Received ({myRatings.length})</h3>
        {myRatings.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No ratings yet</p>
        ) : (
          <div className="space-y-4">
            {myRatings.map((rating) => (
              <div key={rating.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="font-medium text-gray-900">
                      {rating.lead_info?.lead_name || 'Unknown Customer'}
                    </p>
                    <p className="text-sm text-gray-600">
                      {rating.lead_info?.property_address || 'N/A'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(rating.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    {renderStars(rating.rating)}
                    <p className="text-sm text-gray-600 mt-1">{rating.rating}/5</p>
                  </div>
                </div>
                {rating.comment && (
                  <div className="bg-gray-50 p-3 rounded-md mb-3">
                    <p className="text-sm text-gray-700 italic">"{rating.comment}"</p>
                  </div>
                )}
                {rating.realtor_explanation ? (
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                    <p className="text-xs font-semibold text-blue-900 mb-1">Your Response:</p>
                    <p className="text-sm text-blue-800">{rating.realtor_explanation}</p>
                    <p className="text-xs text-blue-600 mt-1">
                      Submitted: {new Date(rating.explanation_submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setResponseDialog({ open: true, rating })}
                    className="mt-2"
                  >
                    <MessageSquare className="w-4 h-4 mr-1" />
                    Respond to Rating
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Response Dialog */}
      <Dialog open={responseDialog.open} onOpenChange={(open) => {
        if (!open) {
          setResponseDialog({ open: false, rating: null });
          setExplanation('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to Rating</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Provide an explanation or response to this rating. This will be visible to admins.
            </p>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="Enter your explanation..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResponseDialog({ open: false, rating: null });
                setExplanation('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={submitExplanation} className="bg-orange-600 hover:bg-orange-700">
              <Send className="w-4 h-4 mr-1" />
              Submit Response
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rate Customer Dialog */}
      <Dialog open={rateCustomerDialog.open} onOpenChange={(open) => {
        if (!open) {
          setRateCustomerDialog({ open: false, lead: null });
          setCustomerRating(0);
          setCustomerComment('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rate Customer</DialogTitle>
          </DialogHeader>
          {rateCustomerDialog.lead && (
            <div className="space-y-4">
              <div>
                <p className="font-medium text-gray-900">{rateCustomerDialog.lead.lead_name}</p>
                <p className="text-sm text-gray-600">{rateCustomerDialog.lead.property_address}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rating (1-5 stars)
                </label>
                {renderStars(customerRating, true, setCustomerRating)}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comments (optional)
                </label>
                <Textarea
                  value={customerComment}
                  onChange={(e) => setCustomerComment(e.target.value)}
                  placeholder="Share your experience working with this customer..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRateCustomerDialog({ open: false, lead: null });
                setCustomerRating(0);
                setCustomerComment('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={submitCustomerRating} className="bg-orange-600 hover:bg-orange-700">
              <Star className="w-4 h-4 mr-1" />
              Submit Rating
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RealtorRatings;
